import React, { useRef, useState } from "react";
import { api, API } from "../lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable image upload widget.
 * Props:
 *   value: current image URL (absolute or /api/files/... path)
 *   onChange: (newUrl: string) => void
 *   label: optional label above the control
 *   aspect: "square" | "video" | "wide" (preview box)
 *   testId: optional data-testid prefix
 */
export default function ImageUpload({
  value,
  onChange,
  label = "Image",
  aspect = "square",
  testId = "image-upload",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const aspectClass =
    aspect === "square"
      ? "aspect-square w-32"
      : aspect === "wide"
        ? "aspect-[16/9] w-full max-w-md"
        : "aspect-video w-full max-w-md";

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Backend returns a relative /api/files/... URL. Resolve to absolute for <img>.
      const absolute = data.url.startsWith("http")
        ? data.url
        : `${API.replace(/\/api$/, "")}${data.url}`;
      onChange(absolute);
      toast.success("Image uploaded");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-start gap-4" data-testid={testId}>
      <div
        className={`${aspectClass} relative shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[#0B1020]`}
      >
        {value ? (
          <img src={value} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-white/40">
            No image
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Loader2 className="h-6 w-6 animate-spin text-white" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-3">
        {label && (
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-white/50">
            {label}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testId}-btn`}
            className="group relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
          >
            <span className="absolute inset-0 rounded-full gradient-bg" />
            <span className="relative flex items-center gap-1.5">
              <Upload size={12} /> {uploading ? "Uploading…" : value ? "Replace" : "Upload image"}
            </span>
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testId}-remove`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs text-white/70 hover:border-white/30 hover:text-white"
            >
              <X size={12} /> Remove
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
        <p className="text-[11px] text-white/40">
          PNG, JPG, WebP, GIF, SVG — up to 10 MB. You can also paste a URL below.
        </p>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... (or upload above)"
          data-testid={`${testId}-url-input`}
          className="w-full rounded-lg border border-white/10 bg-[#0B1020] px-3 py-2 text-xs text-white/80 placeholder:text-white/30 focus:border-fuchsia-500/50 focus:outline-none"
        />
      </div>
    </div>
  );
}
